import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "../i18n";
import { MeshBackground } from "./MeshBackground";

const IMG_GREENHOUSE =
  "https://images.unsplash.com/photo-1524486361537-8ad15938e1a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhZ3JpY3VsdHVyZSUyMGdyZWVuaG91c2UlMjBleHBvcnR8ZW58MHx8fHwxNzg5NTA0MjE5fDA&ixlib=rb-4.1.0&q=85";
const IMG_PORT =
  "https://images.unsplash.com/photo-1613690399151-65ea69478674?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwzfHxjYXJnbyUyMHNoaXAlMjBwb3J0JTIwY29udGFpbmVyJTIwdHJhZGV8ZW58MHx8fHwxNzg5NTA0MjE5fDA&ixlib=rb-4.1.0&q=85";
const IMG_WHEAT = "https://images.unsplash.com/photo-1500382017468-9049fed747ef?q=85&w=900&auto=format&fit=crop";
const CHAPTER_IMGS = [IMG_WHEAT, IMG_PORT, IMG_GREENHOUSE];

const reveal = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
};

export const AboutManifesto = () => {
  const { t } = useLanguage();
  const [hoverImg, setHoverImg] = useState(null);
  const floatRef = useRef(null);

  const handleMove = (e) => {
    const el = floatRef.current;
    if (!el) return;
    const rect = e.currentTarget.getBoundingClientRect();
    el.style.transform = `translate3d(${e.clientX - rect.left + 28}px, ${e.clientY - rect.top - 96}px, 0)`;
  };

  return (
    <section id="kurumsal" data-testid="about-section" className="relative bg-white py-24 sm:py-32">
      <MeshBackground />
      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <motion.p {...reveal} className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-[#1d8a52]">
            {t.about.overline}
          </motion.p>
          <motion.h2
            {...reveal}
            transition={{ ...reveal.transition, delay: 0.08 }}
            className="font-display mt-4 text-2xl font-bold leading-tight tracking-tight text-[#062316] sm:text-3xl lg:text-4xl"
            data-testid="about-title"
          >
            {t.about.title}
          </motion.h2>
          <motion.p
            {...reveal}
            transition={{ ...reveal.transition, delay: 0.16 }}
            className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-slate-600"
          >
            {t.about.intro}
          </motion.p>
        </div>

        <div className="mt-16 grid gap-16 lg:grid-cols-12">
          <div className="relative lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <motion.div {...reveal} className="relative">
                <div className="absolute -left-3 -top-3 h-full w-full rounded-2xl border border-[#48e38e]" />
                <img
                  src={IMG_GREENHOUSE}
                  alt={t.about.captionA}
                  className="relative aspect-[4/5] w-full rounded-2xl object-cover"
                />
                <span className="absolute bottom-4 left-4 rounded-full bg-[#04120b]/80 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#48e38e] backdrop-blur-sm">
                  {t.about.captionA}
                </span>
              </motion.div>
              <motion.div
                {...reveal}
                transition={{ ...reveal.transition, delay: 0.15 }}
                className="relative z-10 -mt-16 ml-auto w-2/3 sm:-mt-24"
              >
                <img
                  src={IMG_PORT}
                  alt={t.about.captionB}
                  className="aspect-[4/3] w-full rounded-2xl border-4 border-white object-cover shadow-2xl"
                />
                <span className="absolute bottom-3 left-3 rounded-full bg-[#04120b]/80 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.18em] text-[#48e38e] backdrop-blur-sm">
                  {t.about.captionB}
                </span>
              </motion.div>
            </div>
          </div>

          <div className="lg:col-span-7 lg:pl-8">
            <div className="relative lg:mt-2" onMouseMove={handleMove}>
              <AnimatePresence>
                {hoverImg && (
                  <div
                    ref={floatRef}
                    className="pointer-events-none absolute left-0 top-0 z-30 hidden lg:block"
                    aria-hidden="true"
                  >
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.85 }}
                      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                    >
                      <img
                        src={hoverImg}
                        alt=""
                        className="h-44 w-64 rounded-2xl border-4 border-white object-cover shadow-[0_24px_60px_-16px_rgba(6,35,22,0.4)]"
                      />
                    </motion.div>
                  </div>
                )}
              </AnimatePresence>

              {t.about.chapters.map((ch, i) => (
                <motion.div
                  key={ch.num}
                  {...reveal}
                  transition={{ ...reveal.transition, delay: 0.1 * i }}
                  data-cursor
                  onMouseEnter={() => setHoverImg(CHAPTER_IMGS[i])}
                  onMouseLeave={() => setHoverImg(null)}
                  className="group grid grid-cols-[auto_1fr] gap-6 border-t border-slate-200 py-8 transition-colors duration-300 last:border-b hover:bg-[#f4fcf7] sm:gap-10"
                  data-testid={`about-chapter-${ch.num}`}
                >
                  <span className="font-mono text-3xl font-semibold text-[#48e38e] transition-transform duration-500 group-hover:-translate-y-1 sm:text-4xl">
                    {ch.num}
                  </span>
                  <div>
                    <h3 className="font-display text-xl font-semibold text-[#062316] sm:text-2xl">{ch.title}</h3>
                    <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate-600 sm:text-base">{ch.text}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
